import React, { useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../app/hooks";
import { updateMember } from "../../features/members/slice-extensions/members-slice-extension";
import { Member } from "../../features/members/models/Member";

interface MemberNotesCardProps {
  member: Member;
}

/**
 * The notes card on the member details page, editable in place — no detour
 * through the full edit form just to jot something down.
 */
const MemberNotesCard: React.FC<MemberNotesCardProps> = ({ member }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const startEditing = () => {
    setDraft(member.notes ?? "");
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    // The update contract carries the whole member; timezone follows the
    // device, exactly as the full edit form saves it (see saveMember.ts).
    const result = await dispatch(updateMember({
      id: member.id,
      request: {
        firstName: member.firstName,
        lastName: member.lastName || null,
        primaryLanguageCode: member.primaryLanguageCode,
        timeZoneName: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timeZoneUtcOffsetHours: new Date().getTimezoneOffset() / -60,
        email: member.email,
        notes: draft.trim() || null,
        address: member.address,
        dateOfBirth: member.dateOfBirth,
        guardianMemberId: member.guardianMemberId,
      },
    }));
    setSaving(false);
    if (updateMember.fulfilled.match(result)) {
      setEditing(false);
    }
  };

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-grey-10 dark:text-grey-90">
          {t("members.detailsPage.notes", "Notes")}
        </h3>
        {!editing && (
          <button type="button" className="btn btn-text" onClick={startEditing}>
            {member.notes ? <Pencil size={16} /> : <Plus size={16} />}
            <span>{member.notes ? t("common.edit") : t("members.detailsPage.addNote")}</span>
          </button>
        )}
      </div>

      {editing ? (
        <div className="space-y-3">
          <textarea
            className="input min-h-24 w-full"
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="flex items-center gap-3">
            <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
              {saving ? t("common.saving") : t("common.save")}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      ) : member.notes ? (
        <p className="whitespace-pre-wrap text-grey-20 dark:text-grey-80">{member.notes}</p>
      ) : (
        <p className="text-sm text-grey-30 dark:text-grey-70">
          {t("members.detailsPage.noData", "No data")}
        </p>
      )}
    </div>
  );
};

export default MemberNotesCard;
